// Nodes: the foundry roadmap lane — qualification, yield research, reliability.

import { baselineDefectDensity, defectDensity, waferCost } from "../../engine/formulas/yieldModel.js";
import type { ContentPack, PlayerView } from "../../engine/types.js";
import type { ScreenProps } from "./shared.js";
import { money, monthLabel, pct } from "../format.js";
import { Bar, Stepper, useMeasuredWidth } from "../components/widgets.js";

/** A horizontal timeline strip — the roadmap as a diagram (position =
 *  availableMonth) rather than a table, above the detailed per-node panels
 *  where the actual qualification/research/reliability controls live. */
function NodeRoadmap({ content, view }: { content: ContentPack; view: PlayerView }) {
  const [wrapRef, measured] = useMeasuredWidth();
  const { startMonthIndex, endMonthIndex } = content.scenario;
  const span = Math.max(1, endMonthIndex - startMonthIndex);
  const width = measured > 0 ? Math.min(680, measured) : 680;
  const pad = 8;
  const trackY = 20;
  const at = (month: number) => pad + Math.max(0, Math.min(1, (month - startMonthIndex) / span)) * (width - pad * 2);
  const nodes = [...content.nodes].sort((a, b) => a.availableMonth - b.availableMonth);
  return (
    <div className="panel">
      <div className="title">FAB ROADMAP</div>
      <div ref={wrapRef}>
        <svg width={width} height={44}>
        <line x1={pad} x2={width - pad} y1={trackY} y2={trackY} stroke="var(--border-bright)" strokeWidth={1} />
        <line x1={at(view.monthIndex)} x2={at(view.monthIndex)} y1={4} y2={trackY + 6} stroke="var(--amber)" strokeDasharray="2,2" />
        {nodes.map((n) => {
          const access = view.nodes[n.id]!;
          const available = n.availableMonth <= view.monthIndex;
          const color =
            access.status === "qualified" ? "var(--ink)" : access.status === "qualifying" ? "var(--amber)" : available ? "var(--ink-faint)" : "var(--border-bright)";
          const x = at(n.availableMonth);
          return (
            <g key={n.id}>
              <circle cx={x} cy={trackY} r={4} fill={color} />
              <text x={x} y={trackY + 16} fill="var(--ink-dim)" fontSize="9" fontFamily="var(--font)" textAnchor="middle">
                {n.featureNm}nm
              </text>
            </g>
          );
        })}
        </svg>
      </div>
      <span className="hint">position = foundry availability month · amber dash = today</span>
    </div>
  );
}

export function Nodes({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const m = view.monthIndex;
  const models = content.economy.models;

  return (
    <div>
      <h2>PROCESS NODES — the world advances on schedule whether or not you're ready</h2>
      <NodeRoadmap content={content} view={snap.view} />
      {[...content.nodes].sort((a, b) => a.availableMonth - b.availableMonth).map((n) => {
        const access = view.nodes[n.id]!;
        const available = n.availableMonth <= m;
        const yourD0 = defectDensity(n, m, access.researchEm, models);
        const baseD0 = baselineDefectDensity(n, m);
        const qualQueued = pending.some((c) => c.type === "startNodeQual" && c.nodeId === n.id);
        const researchQueued = pending.find((c) => c.type === "setYieldResearch" && c.nodeId === n.id);
        const relQueued = pending.find((c) => c.type === "setReliability" && c.nodeId === n.id);
        if (!available && n.availableMonth > m + 36) return null; // keep the far future off-screen
        return (
          <div className="panel" key={n.id}>
            <div className="title">
              {n.displayName.toUpperCase()}{" "}
              <span className={`badge ${access.status === "qualified" ? "cyan" : access.status === "qualifying" ? "amber" : available ? "" : "dim"}`}>
                {available ? access.status.toUpperCase() : `FOUNDRY ETA ${monthLabel(n.availableMonth)}`}
              </span>
            </div>
            <div className="row">
              <div className="tile">
                <div className="k">WAFER ({n.waferDiameterMm}mm)</div>
                <div className="v">{available ? money(waferCost(n, m)) : money(n.waferCostStart)}</div>
                <div className="sub">mask set {money(n.maskSetCost)}</div>
              </div>
              <div className="tile">
                <div className="k">DENSITY</div>
                <div className="v">{n.gateDensity >= 1000 ? `${(n.gateDensity / 1000).toFixed(0)}K` : n.gateDensity}/mm²</div>
                <div className="sub">fab cycle {n.fabCycleMonths}mo</div>
              </div>
              <div className="tile">
                <div className="k">DEFECTS (D0)</div>
                <div className="v">{available ? yourD0.toFixed(2) : n.d0Intro.toFixed(2)}</div>
                <div className="sub">
                  {access.status === "qualified" ? `industry best ${baseD0.toFixed(2)} — research closes the gap` : "defects/cm²"}
                </div>
              </div>
              <div className="tile">
                <div className="k">QUALIFICATION</div>
                <div className="v">{money(n.qual.fee)}</div>
                <div className="sub">
                  {n.qual.engMonths} impl EM · {n.qual.durationMonths}mo
                </div>
              </div>
            </div>

            {access.status === "locked" && available && (
              <button className={`act warn${qualQueued ? " queued" : ""}`} onClick={() => session.queue({ type: "startNodeQual", nodeId: n.id })}>
                {qualQueued ? "QUAL QUEUED" : `START QUALIFICATION — ${money(n.qual.fee)}`}
              </button>
            )}

            {access.status === "qualifying" && (
              <Bar
                frac={1 - access.qualEmRemaining / n.qual.engMonths}
                label={`qualifying — ${access.qualEmRemaining.toFixed(0)} EM + ${access.qualMonthsRemaining}mo remaining`}
                right="draws implementation engineers first"
              />
            )}

            {access.status === "qualified" && (
              <div className="row">
                <div style={{ flex: "1 1 300px" }}>
                  <div className="bar-label">
                    <span>
                      YIELD RESEARCH — closes the {pct(models.yieldGapIntro, 0)} D0 gap (τ={models.yieldGapTauEm} EM)
                    </span>
                    <span>{access.researchEm.toFixed(0)} EM banked</span>
                  </div>
                  <Bar frac={access.researchEm / (5 * models.yieldGapTauEm)} />
                  <Stepper
                    value={access.researchStaff}
                    queued={researchQueued && researchQueued.type === "setYieldResearch" ? researchQueued.engineers : undefined}
                    max={20}
                    onChange={(v) => session.queue({ type: "setYieldResearch", nodeId: n.id, engineers: v })}
                  />
                  <span className="hint"> impl engineers</span>
                </div>
                <div style={{ flex: "1 1 300px" }}>
                  <div className="bar-label">
                    <span>RELIABILITY / QUAL RIGOR — buys down field failures (τ={models.reliabilityTauEm} EM)</span>
                    <span>{access.reliabilityEm.toFixed(0)} EM banked</span>
                  </div>
                  <Bar frac={access.reliabilityEm / (5 * models.reliabilityTauEm)} amber />
                  <Stepper
                    value={access.reliabilityStaff}
                    queued={relQueued && relQueued.type === "setReliability" ? relQueued.engineers : undefined}
                    max={20}
                    onChange={(v) => session.queue({ type: "setReliability", nodeId: n.id, engineers: v })}
                  />
                  <span className="hint"> impl engineers</span>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <span className="hint">
        Skipping yield research is a permanent defect-density penalty on that node. Skipping reliability means recalls at volume — the most
        expensive events in the game.
      </span>
    </div>
  );
}
